import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faCheck, faSpinner, faSave } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './DryRunAssignmentReview.css';

/**
 * Interactive review table for assign-creatives dry-run results.
 *
 * Renders inside the campaign activity feed when:
 *   message.action === 'assign-creatives' && json_response.details.dry_run === true
 *
 * Lets the user edit the LLM-proposed assignments — remove a creative
 * from a line item, add an unassigned creative to a line item — then
 * submit the edited list via "Approve & Apply", which fires a NEW
 * assign-creatives message with `dry_run=false` and the user-overridden
 * assignments. The original dry-run message stays in the feed for audit.
 *
 * State persistence: a Save button uploads the current edits to a GCS
 * sidecar (`campaigns/{id}/dry_run_overrides/{messageId}.json`). On mount,
 * the component fetches the sidecar and — if present — hydrates the table
 * from it instead of the original LLM proposal. One sidecar per dry-run
 * message; re-saving overwrites.
 *
 * Validation: soft warning when creative dimensions don't match the
 * line item's placement size — does NOT block submission.
 */
// Format a creative.dimensions object for the chip / select-option labels.
// Returns an empty string when either dimension is missing — previously we
// only checked that `dimensions` was truthy, which made empty
// `{ widthPixels: undefined, heightPixels: undefined }` payloads render as
// "undefined×undefined". The chip is gated on a truthy dims value, so
// returning '' here hides the badge entirely.
function formatDims(dimensions) {
  const w = dimensions?.widthPixels;
  const h = dimensions?.heightPixels;
  if (w == null || h == null) return '';
  return `${w}×${h}`;
}

const DryRunAssignmentReview = ({ campaign, message, accountId, user, onApplied }) => {
  const details = message?.json_response?.details || {};
  const summary = details.summary || {};

  // Build the canonical creative lookup. Two launcher shapes are supported:
  //
  //   DV360 (current): `details.assignments.{matched, unmatched_creatives}`.
  //     Real DV360 creative IDs live on matched[].asset_ids and
  //     unmatched_creatives[].asset_id. The only human-readable label is
  //     the MMGY-formatted placement_name string (creative_placement_names[i]
  //     in matched, paired by index with asset_ids[i]; placement_name in
  //     unmatched_creatives). Dimensions parse out of `as(WxH)`.
  //
  //   TTD: `details.planned_assignments[]`. Each entry has parallel
  //     asset_ids[] and creative_ids[] (real TTD creative IDs after
  //     upload), with either creative_details[] (regular flow — per-asset
  //     "name (WxH)" strings) or a shared placement_name string (MMGY
  //     flow). We key creatives by their real creative_id since that's
  //     the apply-time identity TTD expects.
  // `creativesById` is a multi-key LOOKUP map: each unique creative is
  // intentionally registered under several keys (asset_id_<N> AND the real
  // platform CreativeId). That makes `.size` UNSAFE for "how many
  // creatives?" — it returns the number of lookup keys, not unique
  // creatives, and overcounts. We track canonical IDs alongside so the
  // banner shows the real count. Canonical = the asset_id_<N> when present
  // (everything except legacy DV360 shapes carries one), else the real
  // creativeId.
  const { map: creativesById, canonicalIds: canonicalCreativeIds, realIdByCanonical } = useMemo(() => {
    const m = new Map();
    const canonical = new Set();
    // canonical asset_id_<N> → real platform CreativeId. Lets the unassigned
    // panel check `assignedSet` (which is keyed by real creative IDs from
    // planned_assignments) against canonical-keyed unassigned candidates.
    const realByCanon = new Map();
    const matched = details.assignments?.matched || [];
    for (const entry of matched) {
      const ids = Array.isArray(entry?.asset_ids) ? entry.asset_ids : [];
      const names = Array.isArray(entry?.creative_placement_names) ? entry.creative_placement_names : [];
      ids.forEach((id, i) => {
        const key = String(id);
        canonical.add(key);
        if (!m.has(key)) m.set(key, buildCreativeRecord(key, names[i]));
      });
    }
    const unmatched = details.assignments?.unmatched_creatives || [];
    for (const u of unmatched) {
      if (!u?.asset_id) continue;
      const key = String(u.asset_id);
      canonical.add(key);
      if (!m.has(key)) m.set(key, buildCreativeRecord(key, u.placement_name));
    }
    // TTD shape: planned_assignments[]. Use creative_ids as keys (real TTD
    // creative IDs), displayName from creative_details[i] (regular flow,
    // per-asset "name (WxH)" strings).
    //
    // Intentionally do NOT use entry.placement_name as displayName: the
    // launcher's LLM-based MMGY path stores the LLM's reasoning text in
    // that field, not a real placement name. The creative_results
    // enrichment pass below supplies the real bs(FY26)... names for the
    // MMGY flow.
    const planned = Array.isArray(details.planned_assignments) ? details.planned_assignments : [];
    for (const entry of planned) {
      const cids = Array.isArray(entry?.creative_ids) ? entry.creative_ids : [];
      const aids = Array.isArray(entry?.asset_ids) ? entry.asset_ids : [];
      const detailsArr = Array.isArray(entry?.creative_details) ? entry.creative_details : null;
      // creative_ids are real TTD IDs after upload (dry-run always uploads);
      // fall back to asset_ids only for legacy messages where creative_ids
      // weren't carried through.
      const idsAligned = cids.length === aids.length && cids.length > 0;
      const ids = idsAligned ? cids : (cids.length > 0 ? cids : aids);
      ids.forEach((id, i) => {
        const key = String(id);
        // Canonical identity is asset_id_<N> when available; only fall back
        // to the real creative ID if asset_ids aren't aligned.
        const canon = String(aids[i] ?? id);
        canonical.add(canon);
        if (idsAligned && aids[i] && cids[i] && String(aids[i]) !== String(cids[i])) {
          realByCanon.set(String(aids[i]), String(cids[i]));
        }
        if (!m.has(key)) {
          // Only seed displayName from per-asset creative_details (regular
          // TTD flow). MMGY flow gets enriched below.
          m.set(key, buildCreativeRecord(key, detailsArr ? detailsArr[i] : null));
        }
      });
    }
    // TTD MMGY enrichment from creative_results[]. The launcher saves the
    // upload-API responses in `details.creative_results` — a parallel array
    // (1-indexed via "asset_id_<N>") of per-creative results, each with:
    //   - details.CreativeId   (real TTD creative ID after upload)
    //   - details.Description  (full "bs(FY26).cn(...)...as(WxH)__YYYYMMDD"
    //                           placement string)
    // Iterate, strip the trailing __YYYYMMDD date suffix, and map every
    // entry under BOTH the "asset_id_<N>" label AND the real CreativeId so
    // chip lookups hit regardless of which identifier the row uses.
    //
    // (PR #1684 commit 1 read from `details.creative_assets` instead, but
    // that field isn't present in real TTD MMGY mode-2 outputs — it only
    // exists for the text-file creative-asset-processing flow at
    // ttd_assign_creatives_launcher.py:847. creative_results is the
    // production-data field.)
    const creativeResults = Array.isArray(details.creative_results) ? details.creative_results : [];
    creativeResults.forEach((r, i) => {
      const inner = r?.details;
      if (!inner) return;
      const realId = inner.CreativeId;
      const rawDesc = inner.Description;
      if (!rawDesc) return;
      // Trim "__YYYYMMDD" trailing date suffix the launcher appends.
      const placementName = String(rawDesc).replace(/__\d{8}$/, '');
      // Mirror the launcher's asset-id labelling: the i-th creative_result
      // corresponds to "asset_id_<i+1>". See launcher line 838 for the
      // matching loop:  asset["asset_id"] = f"asset_id_{i+1}"
      const assetIdLabel = `asset_id_${i + 1}`;
      canonical.add(assetIdLabel);
      if (realId) realByCanon.set(assetIdLabel, String(realId));
      const enrichedRec = buildCreativeRecord(realId || assetIdLabel, placementName);
      // Key by both the asset_id label and the real CreativeId so any
      // lookup style works.
      for (const key of [assetIdLabel, realId].filter(Boolean)) {
        const existing = m.get(String(key));
        if (!existing || !existing.displayName) {
          m.set(String(key), { ...enrichedRec, creativeId: String(key) });
        }
      }
    });
    // asset_placement_names enrichment. The launcher saves a flat
    // {asset_id_<N>: placement_name} dict at details.asset_placement_names
    // covering ALL assets (matched + unmatched). Use it as a fallback so
    // matched chips show "bs(FY26).cn(...).as(WxH)" instead of bare
    // "Creative asset_id_13" labels for any asset not enriched by the
    // creative_results pass above.
    const assetPlacementNames = details.asset_placement_names;
    if (assetPlacementNames && typeof assetPlacementNames === 'object') {
      for (const [assetId, name] of Object.entries(assetPlacementNames)) {
        if (!name) continue;
        const aidKey = String(assetId);
        canonical.add(aidKey);
        const existing = m.get(aidKey);
        if (!existing || !existing.displayName) {
          m.set(aidKey, buildCreativeRecord(aidKey, name));
        }
      }
    }
    // DV360 fetch-from-platform shape: the launcher writes a flat
    // {asset_id_<N>: <real_creative_id>} map at
    // creative_results.response.asset_id_to_creative_id (see
    // dv360_campaign_launcher.py:159) so the apply step can translate back.
    // Without this, resolveCreativeId falls through to a positional lookup
    // against creative_results.response.creatives[idx] — but that list is
    // NOT in asset_id order, so chips render the wrong creatives.
    // Mirror the data into creativesById keyed by BOTH asset_id_<N> AND
    // the real DV360 creative ID — chip rendering hits whichever the row
    // uses. Names come from the parallel asset_placement_names map when
    // present, else from the matched-entry placement names seeded above.
    const flatMap = details.asset_id_to_creative_id
      || details.creative_results?.response?.asset_id_to_creative_id;
    if (flatMap && typeof flatMap === 'object') {
      const names = details.asset_placement_names
        || details.creative_results?.response?.asset_placement_names
        || {};
      for (const [assetId, realId] of Object.entries(flatMap)) {
        if (!realId) continue;
        canonical.add(String(assetId));
        realByCanon.set(String(assetId), String(realId));
        const placement = names[assetId] || '';
        // Only seed creativesById if we have a placement name to contribute.
        // Without one, buildCreativeRecord produces a no-displayName record
        // that would shadow the legacy fallback's full DV360 record
        // (keyed by the real creativeId), forcing chips back to the bare
        // "Creative <id>" label. The legacy block below will populate the
        // realId key with the platform's full creative record (incl. the
        // .as(WxH)_pkid_<date> displayName).
        if (!placement) continue;
        for (const k of [String(assetId), String(realId)].filter(Boolean)) {
          const existing = m.get(k);
          if (!existing || !existing.displayName) {
            m.set(k, { ...buildCreativeRecord(k, placement), creativeId: String(realId) });
          }
        }
      }
    }
    // Legacy fallback: older messages stored the full DV360 creatives list
    // under creative_results.response.creatives (a different shape from the
    // TTD list above). Keep this so historical DV360 dry-runs in the
    // activity feed still render with full metadata.
    //
    // Count discipline: `canonical` is meant to hold ONE id per unique
    // creative (the asset_id_<N> when we have one, else the real id) — the
    // summary banner divides by it. The asset_id_to_creative_id flat-map
    // block above already added asset_id_<N> to canonical AND populated
    // realByCanon: <assetId> → <realId>. Adding the real id to canonical
    // here would double-count every DV360 creative (manifested as "68
    // creatives" for a 34-creative dry-run). Only add the real id to
    // canonical when it ISN'T already represented by an asset_id mapping.
    // We DO still register the real id in `m` either way so chip rendering
    // (keyed by the real id) finds the full DV360 record with displayName +
    // dimensions.
    const legacy = (details.creative_results && !Array.isArray(details.creative_results))
      ? (details.creative_results?.response?.creatives || [])
      : [];
    const realIdsCoveredByCanonical = new Set();
    for (const realId of realByCanon.values()) realIdsCoveredByCanonical.add(String(realId));
    for (const c of legacy) {
      if (!c?.creativeId) continue;
      const realIdKey = String(c.creativeId);
      if (!m.has(realIdKey)) m.set(realIdKey, c);
      if (!realIdsCoveredByCanonical.has(realIdKey)) canonical.add(realIdKey);
    }
    return { map: m, canonicalIds: canonical, realIdByCanonical: realByCanon };
  }, [details.assignments, details.planned_assignments, details.creative_results, details.asset_placement_names, details.asset_id_to_creative_id]);

  // Line items lookup — line_items_result.response[] from DV360 (the LI
  // record carries lineItemId + displayName).
  const lineItemsById = useMemo(() => {
    const arr = details.line_items_result?.response || [];
    const m = new Map();
    for (const li of arr) {
      const id = li?.lineItemId || li?.id;
      if (id) m.set(String(id), li);
    }
    return m;
  }, [details.line_items_result]);

  // Insertion-order lookup — insertion_orders_result.response[] from DV360.
  // Used to group line items under their parent IO in the table. The
  // launcher's IO fetch is soft-fail (see dv360_assign_creatives_launcher.py),
  // so this map can be empty even on a successful dry-run; the render path
  // treats an empty map as "no IO grouping" and renders the flat table.
  const iosById = useMemo(() => {
    const arr = details.insertion_orders_result?.response || [];
    const m = new Map();
    for (const io of arr) {
      const id = io?.insertionOrderId || io?.id;
      if (id) m.set(String(id), io);
    }
    return m;
  }, [details.insertion_orders_result]);

  // Campaign lookup — campaigns_result.response[] from the TTD launcher.
  // TTD's hierarchy is campaign → ad group (no insertion orders), so the
  // dry-run preview groups ad groups under their parent campaign using
  // this map + the adgroup_to_campaign map below. Both are written by
  // ttd_assign_creatives_launcher.py at the fetch-ad-groups step. Same
  // "soft-fail → empty map → flat layout" contract as iosById.
  const campaignsById = useMemo(() => {
    const arr = details.campaigns_result?.response || [];
    const m = new Map();
    for (const cam of arr) {
      const id = cam?.campaign_id || cam?.campaignId || cam?.id;
      if (id) m.set(String(id), cam);
    }
    return m;
  }, [details.campaigns_result]);

  // Platform-aware labels (DV360 → "line item", TTD → "ad group", etc.)
  // shared by the column header and the apply-confirm dialog so they
  // stay in lockstep.
  const platformLabel = useMemo(
    () => labelForDestination(campaign?.destination),
    [campaign?.destination],
  );

  // Adgroup → campaign map (TTD). Lets the row grouping resolve a row's
  // parent campaign without joining back through campaignsById's per-
  // campaign adgroup list.
  const adgroupToCampaign = useMemo(() => {
    const raw = details.adgroup_to_campaign;
    const m = new Map();
    if (raw && typeof raw === 'object') {
      for (const [agId, camId] of Object.entries(raw)) {
        if (agId && camId) m.set(String(agId), String(camId));
      }
    }
    return m;
  }, [details.adgroup_to_campaign]);

  // Build editable rows from the UNION of (line items returned by DV360,
  // assignments proposed by the LLM). Why both:
  //
  //   • Line items the LLM didn't propose for → still need an empty row
  //     so the user can manually assign creatives to them.
  //   • Assignments the LLM proposed for line items not in the response
  //     (rare — possible when fetch raced) → keep the row so we don't
  //     silently drop the LLM's intent.
  //
  // Keying rows by `li-<lineItemId>` keeps state stable across edits and
  // gives the inverse "Assign to…" picker (on the unassigned panel below)
  // a stable rowKey to target.
  const initialAssignments = useMemo(() => {
    const byLineItemId = new Map();

    // 1. Seed with every line item the platform returned, with empty creatives.
    for (const [liId, li] of lineItemsById.entries()) {
      byLineItemId.set(liId, {
        rowKey: `li-${liId}`,
        lineItemId: liId,
        lineItemName: li?.displayName || li?.name || `Line Item ${liId}`,
        creativeIds: [],
        reasoning: '',
      });
    }

    // 2. Merge in proposed assignments. Source priority:
    //    a. details.reasoning.parsed_response.assignments (legacy)
    //    b. details.assignments.matched (current DV360 launcher shape)
    //    c. details.assignments as a plain array (oldest legacy shape)
    //    d. details.planned_assignments (current TTD launcher shape — uses
    //       adgroup_id/adgroup_name; we map them onto the same lineItemId/
    //       lineItemName fields the table renders. creative_ids are real
    //       TTD creative IDs after upload — preferred over asset_ids since
    //       they're what the apply-from-dry-run path needs to send back.)
    const fromReasoning = details.reasoning?.parsed_response?.assignments;
    const fromMatched = details.assignments?.matched;
    const fromTopArray = Array.isArray(details.assignments) ? details.assignments : null;
    const fromPlanned = Array.isArray(details.planned_assignments) ? details.planned_assignments : null;
    const llmAssignments = (Array.isArray(fromReasoning) && fromReasoning.length > 0)
      ? fromReasoning
      : (Array.isArray(fromMatched) && fromMatched.length > 0) ? fromMatched
      : (fromTopArray && fromTopArray.length > 0) ? fromTopArray
      : (fromPlanned || []);
    for (const a of llmAssignments) {
      // TTD uses adgroup_id; DV360 uses line_item_id. Accept both — they
      // both identify "the thing creatives get assigned to" on the platform.
      const liId = String(a.line_item_id || a.lineItemId || a.adgroup_id || a.adgroupId || '');
      if (!liId) continue;
      // For TTD planned_assignments, prefer creative_ids (real TTD IDs)
      // over asset_ids (LLM/Excel labels). For DV360 matched entries, the
      // launcher already populates asset_ids with real DV360 creativeIds.
      const rawIds = Array.isArray(a.creative_ids) && a.creative_ids.length > 0
        ? a.creative_ids
        : Array.isArray(a.asset_ids) ? a.asset_ids : [];
      const creativeIds = rawIds.map(rid => resolveCreativeId(rid, details)).filter(Boolean);
      // Reasoning text:
      //   DV360: a.placement_id (launcher misuses this field for reasoning)
      //   TTD MMGY: a.placement_name (the matched MMGY placement string)
      //   any:   a.reasoning (legacy)
      const reasoning = a.reasoning || a.placement_id || a.placement_name || '';
      const lineItemName = a.line_item_name || a.lineItemName
        || a.adgroup_name || a.adgroupName
        || `Line Item ${liId}`;
      const existing = byLineItemId.get(liId);
      if (existing) {
        existing.creativeIds = creativeIds;
        existing.reasoning = reasoning;
      } else {
        byLineItemId.set(liId, {
          rowKey: `li-${liId}`,
          lineItemId: liId,
          lineItemName,
          creativeIds,
          reasoning,
        });
      }
    }

    return Array.from(byLineItemId.values());
  }, [lineItemsById, details]);

  // The set of creative IDs that are currently assigned (across all rows).
  // Anything in creativesById but not here goes in the "Unassigned" panel.
  const [assignments, setAssignments] = useState(initialAssignments);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applied, setApplied] = useState(false);
  const [showAllUnassigned, setShowAllUnassigned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [hydratedFromSave, setHydratedFromSave] = useState(false);
  const UNASSIGNED_PREVIEW_LIMIT = 50;

  // On mount: hydrate from the GCS sidecar if the user has saved edits for
  // this dry-run message before. Falls back to the LLM proposal silently
  // when no sidecar exists or the fetch fails.
  useEffect(() => {
    let cancelled = false;
    const messageId = message?.message_id;
    if (!campaign?.id || !messageId) return undefined;
    (async () => {
      try {
        const res = await api.get(
          getApiUrl(`/api/v2/campaigns/${campaign.id}/dry-run-edits/${messageId}`),
          { params: { accountId, userId: user?.userId || user?.id } }
        );
        if (cancelled) return;
        if (res.data?.exists && Array.isArray(res.data.assignments)) {
          // Rehydrate the editable rows from the saved override. Build a
          // map of lineItemId -> saved row so we can merge into the table
          // structure built from initialAssignments (preserving line items
          // the user didn't touch).
          const savedByLi = new Map();
          for (const s of res.data.assignments) {
            const liId = String(s.line_item_id || s.lineItemId || s.adgroup_id || s.adgroupId || '');
            if (!liId) continue;
            savedByLi.set(liId, {
              creativeIds: Array.isArray(s.creative_ids) ? s.creative_ids.map(String) : [],
              reasoning: s.reasoning || '',
              lineItemName: s.line_item_name || s.lineItemName || s.adgroup_name || s.adgroupName,
            });
          }
          setAssignments(prev => prev.map(row => {
            const saved = savedByLi.get(String(row.lineItemId));
            if (!saved) return row;
            return {
              ...row,
              creativeIds: saved.creativeIds,
              reasoning: saved.reasoning || row.reasoning,
              lineItemName: saved.lineItemName || row.lineItemName,
            };
          }));
          setSavedAt(res.data.savedAt || null);
          setHydratedFromSave(true);
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.warn('[DryRunAssignmentReview] could not load saved edits:', err.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
    // Intentionally only re-fetch when the source dry-run message identity
    // changes — re-hydrating on every assignment edit would clobber the
    // user's in-progress changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id, message?.message_id]);

  // Group editable rows under their natural parent so the table can render
  // a header above each cluster. DV360 groups by insertion order (LI's
  // parent), TTD groups by campaign (ad group's parent). Whichever
  // grouping-source map is populated wins; if neither is populated (legacy
  // data, soft-failed upstream fetch) we collapse everything into a
  // single "no-grouping" bucket (groupId === null) and the render path
  // skips the header row → flat layout.
  //
  // Stable group order: walk assignments in their existing order and
  // emit each new group key the first time it's seen. This keeps the
  // rendered group order tied to the LLM proposal order rather than to
  // whatever order the platform happened to return parents in.
  const assignmentGroups = useMemo(() => {
    const useIoGrouping = iosById.size > 0;
    const useCampaignGrouping = !useIoGrouping && (campaignsById.size > 0 || adgroupToCampaign.size > 0);
    // groupKind labels the header so we can say "(No insertion order)"
    // vs "(No campaign)" for rows that lack a parent — different copy
    // makes the gap easier to diagnose.
    const groupKind = useIoGrouping ? 'io' : (useCampaignGrouping ? 'campaign' : null);

    const order = [];
    const byGroup = new Map();
    for (const row of assignments) {
      let groupId = null;
      if (useIoGrouping) {
        const li = lineItemsById.get(row.lineItemId);
        groupId = li?.insertionOrderId ? String(li.insertionOrderId) : '__no_parent__';
      } else if (useCampaignGrouping) {
        // For TTD, row.lineItemId IS the adgroup_id (the launcher's
        // matched/planned shape maps adgroup_id → lineItemId in
        // initialAssignments).
        const camId = adgroupToCampaign.get(String(row.lineItemId));
        groupId = camId ? String(camId) : '__no_parent__';
      }
      if (!byGroup.has(groupId)) {
        byGroup.set(groupId, []);
        order.push(groupId);
      }
      byGroup.get(groupId).push(row);
    }
    return order.map(groupId => {
      let label = null;
      if (groupId === '__no_parent__') {
        label = useIoGrouping ? '(No insertion order)' : '(No campaign)';
      } else if (groupId && useIoGrouping) {
        const io = iosById.get(groupId);
        label = io?.displayName || `IO ${groupId}`;
      } else if (groupId && useCampaignGrouping) {
        const cam = campaignsById.get(groupId);
        label = cam?.campaign_name || cam?.campaignName || `Campaign ${groupId}`;
      }
      return {
        groupId,
        groupKind,
        groupLabel: label,
        // groupIdDisplay: the bare ID we show next to the label; null
        // when we don't have one (the no-parent bucket).
        groupIdDisplay: (groupId && groupId !== '__no_parent__') ? groupId : null,
        rows: byGroup.get(groupId),
      };
    });
  }, [assignments, lineItemsById, iosById, campaignsById, adgroupToCampaign]);

  const assignedSet = useMemo(() => {
    const s = new Set();
    for (const row of assignments) {
      for (const cid of row.creativeIds) s.add(cid);
    }
    return s;
  }, [assignments]);

  // Unassigned: every creative in the source set that isn't currently
  // attached to any line item. Includes the LLM's reasoning. Source
  // priority: legacy reasoning.parsed_response.unassigned_assets first,
  // then current launcher's assignments.unmatched_creatives.
  const unassignedReasonByAssetId = useMemo(() => {
    const m = new Map();
    const legacy = details.reasoning?.parsed_response?.unassigned_assets || [];
    for (const u of legacy) {
      if (u?.asset_id) m.set(String(u.asset_id), u.reason || '');
    }
    const unmatched = details.assignments?.unmatched_creatives || [];
    for (const u of unmatched) {
      if (u?.asset_id && !m.has(String(u.asset_id))) {
        m.set(String(u.asset_id), u.reason || '');
      }
    }
    return m;
  }, [details]);

  // Iterate canonical IDs only — `creativesById` is multi-keyed for lookup
  // and would emit each creative up to 3 times. For the assigned-or-not
  // check, accept either the canonical asset_id OR its paired real platform
  // creative ID, since `assignedSet` is populated from planned_assignments'
  // creative_ids (real IDs).
  const unassignedCreatives = useMemo(() => {
    const out = [];
    for (const cid of canonicalCreativeIds) {
      const realId = realIdByCanonical.get(cid);
      if (assignedSet.has(cid) || (realId && assignedSet.has(realId))) continue;
      // Prefer the real-id-keyed record (carries the enriched name from
      // creative_results); fall back to the canonical-keyed record.
      const c = (realId && creativesById.get(realId)) || creativesById.get(cid);
      out.push({
        creativeId: cid,
        creative: c,
        reason: unassignedReasonByAssetId.get(cid) || null,
      });
    }
    return out;
  }, [creativesById, canonicalCreativeIds, realIdByCanonical, assignedSet, unassignedReasonByAssetId]);

  // Edit operations.
  const removeCreativeFromAssignment = (rowKey, creativeId) => {
    setAssignments(prev => prev.map(row =>
      row.rowKey === rowKey
        ? { ...row, creativeIds: row.creativeIds.filter(c => c !== creativeId) }
        : row
    ));
  };

  const addCreativeToAssignment = (rowKey, creativeId) => {
    if (!creativeId) return;
    setAssignments(prev => prev.map(row =>
      row.rowKey === rowKey
        ? (row.creativeIds.includes(creativeId)
            ? row
            : { ...row, creativeIds: [...row.creativeIds, creativeId] })
        : row
    ));
  };

  // Persist the current edits to a GCS sidecar so the next page load
  // hydrates from saved state rather than the original LLM proposal.
  // Idempotent — re-saving overwrites the sidecar.
  const handleSave = async () => {
    setSaveError(null);
    if (saving) return;
    const messageId = message?.message_id;
    if (!campaign?.id || !messageId) {
      setSaveError('Cannot save — missing campaign or message id.');
      return;
    }
    // Serialize ALL rows (including emptied ones), so re-load preserves
    // "user explicitly cleared this row" state. The apply step still skips
    // empty rows; saving them here is purely for restoration.
    const payload = assignments.map(row => ({
      line_item_id: row.lineItemId,
      line_item_name: row.lineItemName,
      creative_ids: row.creativeIds,
      reasoning: row.reasoning || '',
    }));
    setSaving(true);
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/dry-run-edits/${messageId}`),
        { assignments: payload },
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setSavedAt(res.data?.savedAt || new Date().toISOString());
      setHydratedFromSave(true);
    } catch (err) {
      console.error('[DryRunAssignmentReview] save failed:', err);
      setSaveError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    setApplyError(null);
    if (applying || applied) return;

    // Platform labels come from the component-scope `platformLabel` memo
    // (see top of the component) so the apply-confirm dialog uses the
    // same vocabulary as the table column header.

    // Build the override payload. Skip empty rows (a target with no
    // creatives left after edits is a no-op for the launcher and we drop
    // it so the result doesn't include zero-creative rows.)
    //
    // Defense-in-depth: run every creativeId through resolveCreativeId
    // one more time. The unassigned panel can render a chip whose key is
    // an "asset_id_<N>" label (because creativesById is dual-keyed under
    // both the label and the real CreativeId for TTD MMGY), and clicking
    // such a chip leaves the label in row.creativeIds. Without this
    // normalization the label flows verbatim into the apply payload and
    // TTD rejects with "Creatives not accessible to Advertiser:
    // asset_id_13, ...". Stripping any unresolved labels (dedup is a
    // safety net for the dual-key seeding) means the apply payload
    // always carries real platform IDs.
    // Strip both kinds of placeholder we ever wrote: the asset_id_<N> label
    // (always present in pre-upload data) and the legacy "dry_run_<asset_id>"
    // string the launcher produced for old preview-only dry-runs. The
    // current flow uploads creatives even in dry-run, so new messages never
    // carry either form; this is back-compat for in-flight legacy messages.
    const seenLabel = (s) => /^asset_id_\d+$/.test(String(s)) || /^dry_run_/.test(String(s));
    const normalizeIds = (ids) => {
      const out = [];
      const seen = new Set();
      for (const id of ids) {
        const resolved = resolveCreativeId(id, details);
        if (!resolved || seenLabel(resolved)) continue;
        if (seen.has(resolved)) continue;
        seen.add(resolved);
        out.push(resolved);
      }
      return out;
    };

    const overridden = assignments
      .map(row => ({ ...row, creativeIds: normalizeIds(row.creativeIds) }))
      .filter(row => row.creativeIds.length > 0)
      .map(row => ({
        line_item_id: row.lineItemId,
        line_item_name: row.lineItemName,
        creative_ids: row.creativeIds,
        reasoning: 'user-overridden via dry-run review',
      }));

    // Surface any unresolved asset-id labels rather than silently dropping
    // them. (Dedup is fine and intentional; only label-leaks are a problem.)
    const unresolved = [];
    for (const row of assignments) {
      for (const id of row.creativeIds) {
        const resolved = resolveCreativeId(id, details);
        if (resolved && seenLabel(resolved) && !unresolved.includes(resolved)) {
          unresolved.push(resolved);
        }
      }
    }
    if (unresolved.length > 0) {
      console.warn('[DryRunAssignmentReview] dropping unresolved placeholder ids from apply payload:', unresolved);
      setApplyError(
        `Could not resolve real creative IDs for: ${unresolved.join(', ')}. ` +
        `These look like dry-run placeholders from an older flow — re-run the assignment so creatives upload, then apply from the new dry-run.`,
      );
      return;
    }

    if (overridden.length === 0) {
      const proceed = window.confirm(
        `You have no creatives assigned to any ${platformLabel.singular}. Apply will run with zero assignments. Continue?`
      );
      if (!proceed) return;
    } else {
      const totalCreatives = overridden.reduce((n, r) => n + r.creative_ids.length, 0);
      const proceed = window.confirm(
        `Apply ${totalCreatives} creative assignment${totalCreatives === 1 ? '' : 's'} `
        + `across ${overridden.length} ${overridden.length === 1 ? platformLabel.singular : platformLabel.plural}?\n\n`
        + `This will modify the live campaign on ${platformLabel.platform}.`
      );
      if (!proceed) return;
    }

    setApplying(true);
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/apply-creative-assignments`),
        {
          sourceMessageId: message.message_id,
          assignments: overridden,
        },
        {
          params: {
            accountId,
            userId: user?.userId || user?.id,
          },
        }
      );
      setApplied(true);
      if (typeof onApplied === 'function') onApplied(res.data);
    } catch (err) {
      console.error('[DryRunAssignmentReview] apply failed:', err);
      setApplyError(err.response?.data?.error || err.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  // Soft-warning: a creative whose dimensions don't match the line item's
  // expected placement size. The line item record has a `placementSize` /
  // similar field on DV360. We don't always have it; when we do, surface
  // a yellow warning.
  const sizeMismatchForRow = (row) => {
    const li = lineItemsById.get(row.lineItemId);
    const liSize = li?.placementSize || li?.adSize || null; // e.g. {widthPixels, heightPixels}
    if (!liSize) return [];
    const mismatched = [];
    for (const cid of row.creativeIds) {
      const c = creativesById.get(cid);
      if (!c?.dimensions) continue;
      if (c.dimensions.widthPixels !== liSize.widthPixels
          || c.dimensions.heightPixels !== liSize.heightPixels) {
        mismatched.push(cid);
      }
    }
    return mismatched;
  };

  // Hard empty state — campaign has no line items in DV360 at all. The
  // table can't help; the user has to fix the upstream data first. Show a
  // clear callout and still surface the unassigned creatives below so they
  // know what's pending.
  const noLineItems = lineItemsById.size === 0 && assignments.length === 0;

  // Unique-creative count for the banner. `creativesById.size` overcounts
  // because the map is multi-keyed for lookup (asset_id_<N> AND real
  // CreativeId). Prefer the backend's authoritative summary (TTD →
  // creatives_processed, DV360 → creatives_available); fall back to the
  // canonical asset/creative-id Set tracked alongside the map.
  const creativeCount = summary.creatives_available
    ?? summary.creatives_processed
    ?? canonicalCreativeIds.size;

  return (
    <div className="dryrun-assignment-review">
      <div className="dar-summary-row">
        <div className="dar-badge dar-badge-info">DRY RUN PREVIEW</div>
        <div className="dar-summary-numbers">
          <span><strong>{creativeCount}</strong> creatives</span>
          <span><strong>{noLineItems ? 0 : (summary.line_items_receiving_creatives ?? summary.matched_line_items ?? assignments.filter(r => r.creativeIds.length > 0).length)}</strong> {platformLabel.plural} will receive creatives</span>
          <span><strong>{summary.total_creative_assignments ?? assignedSet.size}</strong> total assignments</span>
        </div>
      </div>

      {/* Hard empty: no line items / ad groups under this campaign. */}
      {noLineItems && (
        <div className="dar-empty-callout">
          <h4>No {platformLabel.plural} in this campaign yet</h4>
          <p>
            Creative assignment requires {platformLabel.plural} to assign to. Add {platformLabel.plural} to this campaign in {platformLabel.platform}, then re-run dry-run from the Upload Creatives modal.
          </p>
          <p className="dar-empty-callout-aside">
            The {creativeCount} creative{creativeCount === 1 ? '' : 's'} below {creativeCount === 1 ? 'is' : 'are'} ready to assign once {platformLabel.plural} exist.
          </p>
        </div>
      )}

      {/* Assignments table */}
      {!noLineItems && (
      <div className="dar-table-section">
        <h4>Proposed Assignments</h4>
        {assignments.length === 0 ? (
          <p className="dar-empty">No {platformLabel.plural} received any creative assignments. Use the unassigned panel below to add some manually.</p>
        ) : (
          <table className="dar-table">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>
                  {platformLabel.singular === 'ad group' ? 'Ad Group' : 'Line Item'}
                </th>
                <th style={{ width: '50%' }}>Creatives</th>
                <th style={{ width: '20%' }}>Add Creative</th>
              </tr>
            </thead>
            {assignmentGroups.map(group => (
              <tbody key={group.groupId == null ? '__flat__' : group.groupId}>
                {group.groupLabel && (
                  <tr className="dar-io-header">
                    <td colSpan={3}>
                      <span className="dar-io-header-kind">
                        {group.groupKind === 'io' ? 'IO' : 'Campaign'}
                      </span>
                      <span className="dar-io-header-label" title={group.groupLabel}>
                        {group.groupLabel}
                      </span>
                      {group.groupIdDisplay && (
                        <span className="dar-io-header-id">
                          {group.groupKind === 'io' ? 'IO ID: ' : 'Campaign ID: '}{group.groupIdDisplay}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                {group.rows.map(row => {
                  const mismatchedIds = sizeMismatchForRow(row);
                  return (
                    <tr key={row.rowKey}>
                      <td className="dar-line-item">
                        <div className="dar-line-item-name" title={row.lineItemName}>{row.lineItemName}</div>
                        <div className="dar-line-item-id">ID: {row.lineItemId}</div>
                        {row.reasoning && (
                          <div className="dar-reasoning" title={row.reasoning}>
                            <em>LLM: {row.reasoning.length > 80 ? row.reasoning.slice(0, 80) + '…' : row.reasoning}</em>
                          </div>
                        )}
                      </td>
                      <td className="dar-creatives-cell">
                        {row.creativeIds.length === 0 ? (
                          <span className="dar-empty-inline">No creatives</span>
                        ) : (
                          <div className="dar-chips">
                            {row.creativeIds.map(cid => {
                              const c = creativesById.get(cid);
                              const dims = formatDims(c?.dimensions);
                              const isMismatch = mismatchedIds.includes(cid);
                              return (
                                <span
                                  key={cid}
                                  className={`dar-chip ${isMismatch ? 'dar-chip-warn' : ''}`}
                                  title={isMismatch ? `Dimensions do not match this ${platformLabel.singular}` : (c?.displayName || cid)}
                                >
                                  <span className="dar-chip-name">
                                    {c?.displayName || `Creative ${cid}`}
                                  </span>
                                  {dims && <span className="dar-chip-dims">{dims}</span>}
                                  <button
                                    type="button"
                                    className="dar-chip-remove"
                                    onClick={() => removeCreativeFromAssignment(row.rowKey, cid)}
                                    disabled={applying || applied}
                                    title={`Remove from this ${platformLabel.singular}`}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td>
                        {unassignedCreatives.length === 0 ? (
                          <span className="dar-empty-inline">—</span>
                        ) : (
                          <select
                            className="dar-add-select"
                            value=""
                            onChange={(e) => {
                              addCreativeToAssignment(row.rowKey, e.target.value);
                              e.target.value = '';
                            }}
                            disabled={applying || applied}
                          >
                            <option value="">— Add creative —</option>
                            {unassignedCreatives.map(u => {
                              const dims = formatDims(u.creative?.dimensions);
                              const label = u.creative?.displayName || `Creative ${u.creativeId}`;
                              const optionText = `${label}${dims ? ` (${dims})` : ''}`;
                              return (
                                <option key={u.creativeId} value={u.creativeId} title={optionText}>
                                  {optionText}
                                </option>
                              );
                            })}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        )}
      </div>
      )}

      {/* Unassigned panel — visible regardless of line-items state so the
          user always sees what's pending. */}
      {unassignedCreatives.length > 0 && (
        <div className="dar-unassigned-section">
          <h4>Unassigned Creatives ({unassignedCreatives.length})</h4>
          <p className="dar-unassigned-help">
            {assignments.length > 0
              ? `These creatives are not currently assigned to any ${platformLabel.singular}. Pick a destination from the "Assign to…" picker on each row, or use the "Add Creative" dropdown on a ${platformLabel.singular} row above.`
              : `These creatives have no ${platformLabel.singular} to assign to yet.`}
          </p>
          <ul className="dar-unassigned-list">
            {(showAllUnassigned ? unassignedCreatives : unassignedCreatives.slice(0, UNASSIGNED_PREVIEW_LIMIT)).map(u => {
              const dims = formatDims(u.creative?.dimensions);
              const fullName = u.creative?.displayName || `Creative ${u.creativeId}`;
              return (
                <li key={u.creativeId} className="dar-unassigned-item">
                  <div className="dar-unassigned-row">
                    <div className="dar-unassigned-name" title={fullName}>
                      <FontAwesomeIcon icon={faPlus} className="dar-unassigned-icon" />
                      <span className="dar-unassigned-name-text">
                        {u.creative?.displayName || `Creative ${u.creativeId}`}
                      </span>
                      {dims && <span className="dar-chip-dims" style={{ marginLeft: 8 }}>{dims}</span>}
                    </div>
                    {/* Inverse assign affordance — pick a line-item row
                        from this creative's perspective. Hidden when the
                        table has no rows (otherwise the picker would be
                        empty). */}
                    {assignments.length > 0 && (
                      <select
                        className="dar-add-select dar-unassigned-select"
                        value=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          addCreativeToAssignment(`li-${e.target.value}`, u.creativeId);
                          e.target.value = '';
                        }}
                        disabled={applying || applied}
                        title={`Assign this creative to a ${platformLabel.singular}`}
                      >
                        <option value="">— Assign to {platformLabel.singular} —</option>
                        {assignments.map(row => (
                          <option key={row.rowKey} value={row.lineItemId} title={row.lineItemName}>
                            {row.lineItemName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {u.reason && (
                    <div className="dar-unassigned-reason" title={u.reason}>
                      Reason: {u.reason.length > 100 ? u.reason.slice(0, 100) + '…' : u.reason}
                    </div>
                  )}
                </li>
              );
            })}
            {unassignedCreatives.length > UNASSIGNED_PREVIEW_LIMIT && (
              <li className="dar-unassigned-more">
                <button
                  type="button"
                  className="dar-unassigned-more-btn"
                  onClick={() => setShowAllUnassigned(v => !v)}
                >
                  {showAllUnassigned
                    ? 'Show fewer'
                    : `Show ${unassignedCreatives.length - UNASSIGNED_PREVIEW_LIMIT} more`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Save + Apply controls — hidden when there are no line items
          (nothing to apply against). Save persists the current edits to a
          GCS sidecar so the table can be restored on the next page load;
          Apply dispatches a new assign-creatives message with the edited
          mapping. The dry-run already uploaded the creatives, so Apply
          uses the real creative IDs from the dry-run output directly. */}
      {!noLineItems && (
        <div className="dar-apply-bar">
          {applyError && (
            <div className="dar-apply-error">Apply failed: {applyError}</div>
          )}
          {saveError && (
            <div className="dar-apply-error">Save failed: {saveError}</div>
          )}
          {hydratedFromSave && savedAt && (
            <div className="dar-saved-note" title={savedAt}>
              Loaded saved edits from {new Date(savedAt).toLocaleString()}
            </div>
          )}
          {applied ? (
            <div className="dar-applied-success">
              <FontAwesomeIcon icon={faCheck} /> Assignments applied. A new assign-creatives entry will appear in the activity feed when processing finishes.
            </div>
          ) : (
            <div className="dar-apply-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="dar-save-btn"
                onClick={handleSave}
                disabled={saving || applying}
                title="Save your edits so they are restored on the next page load"
              >
                {saving ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                ) : (
                  <><FontAwesomeIcon icon={faSave} /> Save edits</>
                )}
              </button>
              <button
                type="button"
                className="dar-apply-btn"
                onClick={handleApply}
                disabled={applying || saving}
              >
                {applying ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Applying…</>
                ) : (
                  <><FontAwesomeIcon icon={faCheck} /> Approve &amp; Apply</>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Resolve an LLM-emitted asset ID (e.g., "asset_id_1") to a real DV360
// Resolve an LLM/Excel asset-label ("asset_id_<N>") into the platform's
// real creative ID. Two launcher output shapes are supported:
//
//   TTD MMGY (mode 2): details.creative_results is a flat array; the i-th
//     entry corresponds to "asset_id_<i+1>" and carries
//     details.CreativeId — the real TTD creative ID after upload.
//
//   DV360 (legacy): details.creative_results.response.creatives[idx].creativeId
//     — the real DV360 creativeId. Same 1-indexed convention.
//
// Anything that doesn't match the asset-label pattern is passed through
// unchanged (real platform CreativeIds, etc.).
//
// Returning the asset-label unresolved would let it leak into the apply
// payload — TTD then rejects with "Creatives not accessible to Advertiser:
// asset_id_13, ..." because asset_id_<N> isn't a real ID. So we add a
// final defensive check at the apply boundary too (see normalizeCreativeId
// usage in handleApply).
function resolveCreativeId(rawId, details) {
  if (!rawId) return null;
  const s = String(rawId);
  const m = s.match(/^asset_id_(\d+)$/);
  if (!m) return s;
  const idx = parseInt(m[1], 10) - 1;

  // DV360 fetch-from-platform shape: launcher attaches a flat
  // {asset_id_<N>: <real_creative_id>} dict because the synthetic
  // asset_id labels are only used internally for the LLM prompt; the
  // real DV360 creativeIds need to flow through to the apply step. The
  // launcher writes it at creative_results.response.asset_id_to_creative_id
  // (dv360_campaign_launcher.py:159) — fall back to details root for
  // any future code that might lift it up.
  const flatMap = details?.asset_id_to_creative_id
    || details?.creative_results?.response?.asset_id_to_creative_id;
  if (flatMap && typeof flatMap === 'object' && flatMap[s]) {
    return String(flatMap[s]);
  }

  // TTD MMGY shape: flat array, details.CreativeId per entry.
  if (Array.isArray(details?.creative_results)) {
    const r = details.creative_results[idx];
    const ttdId = r?.details?.CreativeId || r?.details?.creativeId;
    if (ttdId) return String(ttdId);
  }

  // DV360 shape: nested under .response.creatives[].
  const dv360List = details?.creative_results?.response?.creatives || [];
  const c = dv360List[idx];
  if (c?.creativeId) return String(c.creativeId);

  // Couldn't resolve — return the label as-is. The apply boundary will
  // surface this as a bug (asset_id_<N> is not a real creative ID).
  return s;
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Platform-specific labels for the apply-confirm dialog. The component's
// internal state and table rendering use generic "line item" naming
// because that's what the DV360-shaped wire format uses, but the
// user-visible confirm prompt should match the actual platform.
function labelForDestination(destination) {
  switch (destination) {
    case 'ttd':
      return { platform: 'TTD', singular: 'ad group', plural: 'ad groups' };
    case 'dv360':
      return { platform: 'DV360', singular: 'line item', plural: 'line items' };
    default:
      return {
        platform: (destination || 'the platform').toString().toUpperCase(),
        singular: 'line item',
        plural: 'line items',
      };
  }
}

// Synthesize a creative record from an MMGY placement_name string. The
// launcher's dry-run output gives us the asset_id and the placement_name
// but not the DV360 displayName or a full dimensions object — we extract
// what we can so chips render with a name + size, and keep the full
// placement_name as a tooltip (set via `displayName`).
function buildCreativeRecord(creativeId, placementName) {
  const rec = { creativeId };
  if (placementName) {
    rec.displayName = placementName;
    const m = String(placementName).match(/\.as\((\d+)x(\d+)\)/);
    if (m) {
      rec.dimensions = {
        widthPixels: Number(m[1]),
        heightPixels: Number(m[2]),
      };
    }
  }
  return rec;
}

export default DryRunAssignmentReview;
